import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Search, User, Menu, X, ShieldCheck, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { CATEGORIES } from "../lib/categories";

const TICKER = [
  "AUTHORIZED RETAILER",
  "FREE SHIPPING OVER $75",
  "30-DAY RETURNS",
  "MANUFACTURER WARRANTY",
  "9 CATEGORIES · 1 DESTINATION",
];

export default function Header() {
  const { user, logout } = useAuth();
  const { count, setOpen: setCartOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => { setMobileOpen(false); }, [navigate]);

  const onSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/shop?q=${encodeURIComponent(search.trim())}`);
  };

  return (
    <header className="sticky top-0 z-40 border-b border-[#0a0a0a] bg-white" data-testid="site-header">
      {/* Ticker */}
      <div className="bg-[#0a0a0a] text-white overflow-hidden">
        <div className="flex whitespace-nowrap py-2 rh-ticker font-mono text-[11px] tracking-[0.25em]">
          {[...TICKER, ...TICKER, ...TICKER].map((t, i) => (
            <span key={i} className="flex items-center gap-3">
              <span className="inline-block h-1 w-1 bg-white" />
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center gap-6">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
          <div className="bg-[#002fa7] text-white px-2 py-1 font-display font-black text-lg leading-none">ML</div>
          <span className="font-display font-black text-xl tracking-tight">MerchLander<span className="text-[#002fa7]">.</span></span>
        </Link>

        <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-2xl border border-[#0a0a0a]">
          <input
            data-testid="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search 9 categories — e.g. ‘Sony WH-1000XM5’"
            className="flex-1 px-4 py-3 text-sm bg-transparent outline-none"
          />
          <button data-testid="search-submit" type="submit" className="bg-[#0a0a0a] text-white px-5 hover:bg-[#002fa7] transition-colors">
            <Search size={18} />
          </button>
        </form>

        <div className="ml-auto flex items-center gap-2">
          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <Link to="/account" data-testid="account-link" className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#f5f5f5]">
                <User size={16} /> <span className="hidden lg:inline">{user.name?.split(" ")[0] || "Account"}</span>
              </Link>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="admin-link" className="flex items-center gap-2 px-3 py-2 text-sm border border-[#002fa7] text-[#002fa7] hover:bg-[#002fa7] hover:text-white">
                  <ShieldCheck size={16} /> Admin
                </Link>
              )}
              <button data-testid="logout-btn" onClick={logout} className="px-3 py-2 text-sm hover:bg-[#f5f5f5]">
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <Link to="/login" data-testid="login-nav" className="hidden md:flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#f5f5f5]">
              <User size={16} /> Sign in
            </Link>
          )}

          <button
            data-testid="open-cart-btn"
            onClick={() => setCartOpen(true)}
            className="relative flex items-center gap-2 bg-[#0a0a0a] text-white px-4 py-3 hover:bg-[#002fa7] transition-colors"
          >
            <ShoppingBag size={18} />
            <span className="hidden sm:inline text-xs font-semibold tracking-widest uppercase">Cart</span>
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-[#ff3b30] text-white text-[10px] font-bold rh-num h-5 min-w-[20px] px-1 flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          <button data-testid="mobile-menu-btn" className="md:hidden p-2" onClick={() => setMobileOpen((v) => !v)}>
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Category nav */}
      <nav className="hidden md:block border-t border-[#e5e5e5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-1 overflow-x-auto">
          <NavLink to="/shop" end className={({isActive}) => `px-4 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#f5f5f5] ${isActive ? "text-[#002fa7]" : "text-[#0a0a0a]"}`} data-testid="nav-all">
            All
          </NavLink>
          {CATEGORIES.map((c) => (
            <NavLink key={c.slug} to={`/shop/${c.slug}`} className={({isActive}) => `px-4 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#f5f5f5] whitespace-nowrap ${isActive ? "text-[#002fa7]" : "text-[#0a0a0a]"}`} data-testid={`nav-cat-${c.slug}`}>
              {c.short}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-[#e5e5e5] bg-white">
          <form onSubmit={onSearch} className="p-4 border-b border-[#e5e5e5] flex">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…" className="flex-1 border border-[#0a0a0a] px-3 py-2 text-sm outline-none" data-testid="mobile-search-input" />
          </form>
          <div className="flex flex-col">
            {CATEGORIES.map((c) => (
              <Link key={c.slug} to={`/shop/${c.slug}`} className="px-4 py-3 border-b border-[#e5e5e5] text-sm font-semibold uppercase tracking-wider" data-testid={`mobile-nav-${c.slug}`}>
                <span className="font-mono text-[#002fa7] mr-3">{c.code}</span> {c.name}
              </Link>
            ))}
            {user ? (
              <>
                <Link to="/account" className="px-4 py-3 border-b border-[#e5e5e5] text-sm">My Account</Link>
                {user.role === "admin" && <Link to="/admin" className="px-4 py-3 border-b border-[#e5e5e5] text-sm text-[#002fa7]">Admin</Link>}
                <button onClick={logout} className="px-4 py-3 text-left text-sm">Sign Out</button>
              </>
            ) : (
              <Link to="/login" className="px-4 py-3 text-sm">Sign In</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
